import React, { useState, useEffect } from "react";
import { Box, Container } from "@mui/material";
import { createAlert } from "@/features/homepage/alertSliceList";
import { ShepTab, ShepTabs } from "@/components/GenericComponents/ShepTab";
import LoadCollection from "@/components/Integrations/EditSchema/LoadCollection/LoadCollection";
import GlobalSettings from "@/components/Integrations/EditSchema/GlobalSettings/GlobalSettings";
import Mapping from "../EditSchema/Mapping/Mapping";
import TransactionSetSettings from "../EditSchema/TransactionSetSettings/TransactionSetSettings";
import {
  setEditSchemaTabValue,
  setMapTabSelected,
  selectTransactionSettingsDataChangeDetected,
  endpointTypesEnum,
  selectEditConnectionEndpoints,
  selectSelectedConnection,
} from "@/features/connections/integrationSlice";
import { useDispatch, useSelector } from "react-redux";
import { setIsMappingClicked, selectIsMappingClicked } from "@/features/mapSchema/mapSchemaSlice";
import EmailSchemaSetup from "@/components/Integrations/EditSchema/EmailSettings/EmailSchemaSetup";
import EmailSchemaSettings from "@/components/Integrations/EditSchema/EmailSettings/EmailSchemaSettings";
import EmailConnectionSettings from "@/components/Integrations/EditSchema/EmailSettings/EmailConnectionSettings";
import EditQualifiersItems from "@/components/Integrations/EditSchema/GlobalSettings/EditQualifiersItems";
import { SubtabPortal } from "@/components/Integrations/ManageConnection/ManageConnectionPopupContext";

const ConnectionSettingTabs = {
  LoadCollection: 0,
  GlobalSettings: 1,
  TransactionSetSettings: 2,
  Mapping: 3,
};
const EmailSettingsTabs = {
  EmailConnectionSettings: 0,
  GlobalSettings: 1,
  TransactionSetSettings: 2,
};

const Schema = ({}) => {
  const dispatch = useDispatch();

  const [tabValue, setTabValue] = useState(0);
  const transactionSettingsDataChangeDetected = useSelector(
    selectTransactionSettingsDataChangeDetected,
  );
  const selectedConnection = useSelector(selectSelectedConnection);
  const endpoint = useSelector(selectEditConnectionEndpoints).filter(
    (x) => x.EndpointId === selectedConnection.EndpointId,
  )[0];

  const isMappingClicked = useSelector(selectIsMappingClicked);

  const changeTab = (newValue) => {
    if (newValue === 3) {
      dispatch(setMapTabSelected(true));
    } else {
      dispatch(setMapTabSelected(false));
    }
    dispatch(setEditSchemaTabValue(newValue));
    setTabValue(newValue);
  };
  const handleTabChange = (event, newValue) => {
    if (transactionSettingsDataChangeDetected) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message:
            "There are unsaved changes remaining, do you wish to close the window?",
          buttonLabel: "Close",
          onButtonClick: () => changeTab(newValue),
          buttonLabel2: "Cancel",
        }),
      );
    } else {
      changeTab(newValue);
    }
  };

  useEffect(() => {
    if (tabValue !== 3) {
      dispatch(setIsMappingClicked({ isMappingClicked: false }));
    }
  }, [tabValue, dispatch]);

  return (
    <>
          {(endpoint?.ProtocolId ?? 0) === endpointTypesEnum.Email ? (
              <EmailSchemaSettingsTabs />
          ) : (
              <Container
                  sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: "1 1 auto",
                      minHeight: 0,
                  }}
              >
                  <SubtabPortal>
                      <ShepTabs
                          variant="tertiary"
                          value={tabValue}
                          onChange={handleTabChange}
                          style={{
                              marginBottom: "0px",
                              backgroundColor: "var(--default-light)",
                          }}
                      >
                          <ShepTab
                              style={{ minWidth: "260px", margin: "0px 20px" }}
                              variant="tertiary"
                              key={ConnectionSettingTabs.LoadCollection}
                              label={"Schema Collection"}
                          />
                          <ShepTab
                              style={{ minWidth: "260px", margin: "0px 20px" }}
                              variant="tertiary"
                              key={ConnectionSettingTabs.GlobalSettings}
                              label={"Global Settings"}
                          />
                          <ShepTab
                              style={{ minWidth: "320px" }}
                              variant="tertiary"
                              key={ConnectionSettingTabs.TransactionSetSettings}
                              label={"Transaction Settings"}
                          />
                          <ShepTab
                              variant="tertiary"
                              style={{ minWidth: "200px", margin: "0px 20px" }}
                              key={ConnectionSettingTabs.Mapping}
                              label={"Mapping"}
                          />
                      </ShepTabs>
                  </SubtabPortal>
                  <Box
                      sx={{
                          ...(tabValue === ConnectionSettingTabs.Mapping && isMappingClicked
                              ? {
                                  display: "flex",
                                  flexDirection: "column",
                                  flex: "1 1 auto",
                                  minHeight: 0,
                              }
                              : {}),
                      }}
                  >
                      {tabValue === ConnectionSettingTabs.LoadCollection && (
                          <LoadCollection />
                      )}
                      {tabValue === ConnectionSettingTabs.GlobalSettings && (
                          <GlobalSettings />
                      )}
                      {tabValue === ConnectionSettingTabs.TransactionSetSettings && (
                          <TransactionSetSettings />
                      )}
                      {tabValue === ConnectionSettingTabs.Mapping && <Mapping />}
                  </Box>
              </Container>)}
    </>
  );
};
export default Schema;

const EmailSchemaSettingsTabs = () => {
  const dispatch = useDispatch();

  const [tabValue, setTabValue] = useState(0);
  const transactionSettingsDataChangeDetected = useSelector(
    selectTransactionSettingsDataChangeDetected,
  );

  const handleTabChange = (event, newValue) => {
    if (transactionSettingsDataChangeDetected) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message:
            "There are unsaved changes remaining, do you wish to close the window?",
          buttonLabel: "Close",
          onButtonClick: () => setTabValue(newValue),
          buttonLabel2: "Cancel",
        }),
      );
    } else {
      setTabValue(newValue);
    }
  };

  return (
    <Container>
      <Box
        sx={{
          position: "sticky",
          top: 87,
          zIndex: 1700,
          backgroundColor: "var(--default-light)",
        }}
      >
        <ShepTabs
          variant="tertiary"
          value={tabValue}
          onChange={handleTabChange}
          style={{
            marginBottom: "25px",
            backgroundColor: "var(--default-light)",
          }}
        >
          <ShepTab
            style={{ minWidth: "260px", margin: "0px 20px" }}
            variant="tertiary"
            key={EmailSettingsTabs.EmailConnectionSettings}
            label={"1. Email Settings"}
          />
          <ShepTab
            style={{ minWidth: "260px", margin: "0px 20px" }}
            variant="tertiary"
            key={EmailSettingsTabs.GlobalSettings}
            label={"2. Global Settings"}
          />
          <ShepTab
            style={{ minWidth: "260px", margin: "0px 20px" }}
            variant="tertiary"
            key={EmailSettingsTabs.TransactionSetSettings}
            label={"3. Artifact Settings"}
          />
        </ShepTabs>
      </Box>
      <Box>
        {tabValue === EmailSettingsTabs.EmailConnectionSettings && (
          <EmailConnectionSettings />
        )}
        {tabValue === EmailSettingsTabs.GlobalSettings && (
          <div>
            <EmailSchemaSettings isGlobal={true} />
          </div>
        )}
        {tabValue === EmailSettingsTabs.TransactionSetSettings && (
          <EmailSchemaSetup />
        )}
      </Box>
    </Container>
  );
};
